import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getInvoicesRequest: ["payload"], // payload: { params }
  getInvoicesSuccess: ["payload"], // payload: { data }

  reissueInvoiceRequest: ["payload"], // payload: { id }
  reissueInvoiceSuccess: ["payload"], // payload: { newInvoice }

  updateInvoiceRequest: ["payload"], // payload: { data }
  updateInvoiceSuccess: ["payload"], // payload: { updateInvoice }

  invoiceGeneratingRequest: ["payload"], // payload: { data }
  invoiceGeneratingSuccess: ["payload"], // payload: { generateInvoice }
  invoiceGeneratingFailure: ["payload"], // payload: { errors }

  invoicesFailure: ["payload"], // payload: { errors }

  invoicesSetProp: ["key", "value"],

  getInvoicesExportRequest: ["payload"], // payload: { data }
  getInvoicesExportSuccess: ["payload"], // payload: { url }
  getInvoicesExportFailure: ["payload"], // payload: { errors }

  getInvoicesExportForUserRequest: ["payload"], // payload: { data }
  getInvoicesExportHistoryRequest: ["payload"], // payload: { data }

  getInvoiceHistoryRequest: ["payload"], // payload: { params }
  getInvoiceHistorySuccess: ["payload"], // payload: { data }
  getInvoiceHistoryFailure: ["payload"], // payload: { errors }

  getInvoicesFilterOptionsRequest: ["payload"], // payload: { params }
  getInvoicesFilterOptionsSuccess: ["payload"], // payload: { data }
  getInvoicesFilterOptionsFailure: ["payload"], // payload: { data }

  getInvoiceDownloadRequest: ["payload"], // payload: { data }
  getInvoiceDownloadSuccess: ["payload"], // payload: { url }
  getInvoiceDownloadFailure: ["payload"], // payload: { errors }

  clearInvoicesFilters: ["payload"], // payload: { data }

  getInvoicesStatusOptionsRequest: ["payload"], // payload: { params }
  getInvoicesStatusOptionsSuccess: ["payload"], // payload: { data }
  getInvoicesStatusOptionsFailure: ["payload"], // payload: { data }

  logout: null
})

export const InvoicesTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const defaultColumns = [
  { field_id: "date", label: "Date", active: true },
  { field_id: "invoice_amount", label: "Amount", active: true },
  { field_id: "balance_before", label: "Balance Before", active: true },
  { field_id: "balance_after", label: "Balance After", active: true },
  { field_id: "card_last4", label: "Payment Method", active: true },
  { field_id: "paid_with", label: "Paid with", active: true },
  { field_id: "status", label: "Status", active: true },
  { field_id: "topup_type", label: "Type", active: true },
  { field_id: "invoice_id", label: "Transaction ID", active: true }
]

const exceptionalAdminColumns = [{ field_id: "is_reissued", label: "Reissued", active: true }]

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumns: [...defaultColumns],
  columnsAdmin: [...defaultColumns, ...exceptionalAdminColumns],

  filtersLastUpd: null,
  filters: {
    date: null,

    status: "all",
    paid_with: "all",
    is_reissued: "all",

    invoice_id: [],
    card_last4: [],

    invoice_amount: { min: null, max: null, type: null },
    balance_before: { min: null, max: null, type: null },
    balance_after: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    paid_with: [],
    is_reissued: [],
    topup_type: ["Auto Top up", "Manual Top up"]
  },

  fetchingStatusOptions: false,
  statusOptions: [],

  visibleFilters: { status: true, invoice_amount: true, paid_with: true },
  visibleFiltersAdmin: { status: true, invoice_amount: true, paid_with: true },

  exportFetching: false,
  exportResult: null,
  generateInvoice: null,

  downloadFetching: false,

  invoiceDetails: null,
  invoiceHistoryFetching: false,
  invoiceHistory: {
    count: 0,
    next: null,
    previous: null,
    results: []
  }
})

/* ------------- Selectors ------------- */
export const selectAreFiltersDefault = (state) =>
  isEqual(state.invoices.filters, INITIAL_STATE.filters)

/* ------------- Functions for reducer cases ------------- */
const getInvoicesRequest = (state) => state.merge({ fetching: true })
const getInvoicesSuccess = (state, { payload: result }) => {
  if (result.results) {
    return state.merge({ fetching: false, result })
  } else {
    return state.merge({
      fetching: false,
      result: {
        count: result?.length || 0,
        results: result
      }
    })
  }
}

const reissueInvoiceRequest = (state) => state.merge({ fetching: true })
const reissueInvoiceSuccess = (state, { payload: newInvoice }) => {
  return state.merge({
    fetching: false,
    result: {
      count: state.result?.count + 1,
      results: [newInvoice, ...state.result.results]
    }
  })
}

const updateInvoiceRequest = (state) => state.merge({ fetching: true })
const updateInvoiceSuccess = (state, { payload: newInvoice }) => {
  return state.merge({
    fetching: false,
    result: {
      ...state.result,
      results: state.result.results.map((el) => (el.id === newInvoice.id ? newInvoice : el))
    }
  })
}

const invoicesFailure = (state) => state.merge({ fetching: false })

export const invoicesSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

const getInvoicesExportHistoryRequest = (state) => state.merge({ exportFetching: true })
const getInvoicesExportForUserRequest = (state) => state.merge({ exportFetching: true })
const getInvoicesExportRequest = (state) => state.merge({ exportFetching: true })
const getInvoicesExportSuccess = (state, { payload: exportResult }) => {
  const result = typeof exportResult === "boolean" ? {} : exportResult
  return state.merge({
    exportFetching: false,
    exportResult: { type: "Exporting", ...result }
  })
}
const getInvoicesExportFailure = (state) => state.merge({ exportFetching: false })

const getInvoiceDownloadRequest = (state) => state.merge({ downloadFetching: true })
const getInvoiceDownloadSuccess = (state, { payload: exportResult }) => {
  const result = typeof exportResult === "boolean" ? {} : exportResult
  return state.merge({
    downloadFetching: false,
    exportResult: { type: "Downloading", ...result }
  })
}
const getInvoiceDownloadFailure = (state) => state.merge({ downloadFetching: false })

const invoiceGeneratingRequest = (state) => state.merge({ fetching: true })

const invoiceGeneratingSuccess = (state, { payload: generateInvoice }) =>
  state.merge({
    fetching: false,
    generateInvoice
  })

const invoiceGeneratingFailure = (state) => state.merge({ fetching: false })

const getInvoiceHistoryRequest = (state) => state.merge({ invoiceHistoryFetching: true })
const getInvoiceHistorySuccess = (state, { payload: invoiceHistory }) => {
  if (invoiceHistory.results) {
    return state.merge({ invoiceHistoryFetching: false, invoiceHistory })
  } else {
    return state.merge({
      invoiceHistoryFetching: false,
      invoiceHistory: {
        count: invoiceHistory?.length || 0,
        results: invoiceHistory
      }
    })
  }
}
const getInvoiceHistoryFailure = (state) => state.merge({ invoiceHistoryFetching: false })

const getInvoicesFilterOptionsRequest = (state) => state.merge({ fetchingFilterOptions: true })
const getInvoicesFilterOptionsSuccess = (state, { payload: filterOptions }) =>
  state.merge({ fetchingFilterOptions: false, filterOptions })
const getInvoicesFilterOptionsFailure = (state) => state.merge({ fetchingFilterOptions: false })

const getInvoicesStatusOptionsRequest = (state) => state.merge({ fetchingStatusOptions: true })
const getInvoicesStatusOptionsSuccess = (state, { payload: statusOptions }) =>
  state.merge({
    fetchingStatusOptions: false,
    statusOptions: statusOptions?.["status_options"] || []
  })
const getInvoicesStatusOptionsFailure = (state) => state.merge({ fetchingStatusOptions: false })

export const clearInvoicesFilters = (state) => {
  return state.merge({ filters: INITIAL_STATE.filters })
}

const logout = (state) => INITIAL_STATE

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_INVOICES_REQUEST]: getInvoicesRequest,
  [Types.GET_INVOICES_SUCCESS]: getInvoicesSuccess,

  [Types.REISSUE_INVOICE_REQUEST]: reissueInvoiceRequest,
  [Types.REISSUE_INVOICE_SUCCESS]: reissueInvoiceSuccess,

  [Types.UPDATE_INVOICE_REQUEST]: updateInvoiceRequest,
  [Types.UPDATE_INVOICE_SUCCESS]: updateInvoiceSuccess,

  [Types.INVOICES_FAILURE]: invoicesFailure,

  [Types.INVOICES_SET_PROP]: invoicesSetProp,

  [Types.GET_INVOICES_EXPORT_FOR_USER_REQUEST]: getInvoicesExportForUserRequest,
  [Types.GET_INVOICES_EXPORT_HISTORY_REQUEST]: getInvoicesExportHistoryRequest,

  [Types.GET_INVOICES_EXPORT_REQUEST]: getInvoicesExportRequest,
  [Types.GET_INVOICES_EXPORT_SUCCESS]: getInvoicesExportSuccess,
  [Types.GET_INVOICES_EXPORT_FAILURE]: getInvoicesExportFailure,

  [Types.GET_INVOICE_DOWNLOAD_REQUEST]: getInvoiceDownloadRequest,
  [Types.GET_INVOICE_DOWNLOAD_SUCCESS]: getInvoiceDownloadSuccess,
  [Types.GET_INVOICE_DOWNLOAD_FAILURE]: getInvoiceDownloadFailure,

  [Types.INVOICE_GENERATING_REQUEST]: invoiceGeneratingRequest,
  [Types.INVOICE_GENERATING_SUCCESS]: invoiceGeneratingSuccess,
  [Types.INVOICE_GENERATING_FAILURE]: invoiceGeneratingFailure,

  [Types.GET_INVOICE_HISTORY_REQUEST]: getInvoiceHistoryRequest,
  [Types.GET_INVOICE_HISTORY_SUCCESS]: getInvoiceHistorySuccess,
  [Types.GET_INVOICE_HISTORY_FAILURE]: getInvoiceHistoryFailure,

  [Types.GET_INVOICES_FILTER_OPTIONS_REQUEST]: getInvoicesFilterOptionsRequest,
  [Types.GET_INVOICES_FILTER_OPTIONS_SUCCESS]: getInvoicesFilterOptionsSuccess,
  [Types.GET_INVOICES_FILTER_OPTIONS_FAILURE]: getInvoicesFilterOptionsFailure,

  [Types.GET_INVOICES_STATUS_OPTIONS_REQUEST]: getInvoicesStatusOptionsRequest,
  [Types.GET_INVOICES_STATUS_OPTIONS_SUCCESS]: getInvoicesStatusOptionsSuccess,
  [Types.GET_INVOICES_STATUS_OPTIONS_FAILURE]: getInvoicesStatusOptionsFailure,

  [Types.CLEAR_INVOICES_FILTERS]: clearInvoicesFilters,

  [Types.LOGOUT]: logout
})
